import { Typography } from '@workspace/ui/components/Typography';
import { memo, type ReactNode } from 'react';

import { IconButton } from '~/components/isolated/IconButton';

/**
 * Props for the AuthHeader component.
 */
interface AuthHeaderProps {
  /** Main title text displayed in the header */
  title: string;
  /** Subtitle content - can be a string or custom React node */
  subtitle: string | ReactNode;
  /** Optional callback for close button click */
  onClose?: () => void;
}

/**
 * Header component for authentication modal views.
 * Displays a title, subtitle, and optional close button.
 */
export const AuthHeader = memo(
  ({ title, subtitle, onClose }: AuthHeaderProps) => {
    return (
      <div className="flex w-full flex-col items-center gap-6 md:items-start">
        <div className="flex w-full items-center justify-center md:items-start md:justify-between">
          <Typography.Header300Medium>{title}</Typography.Header300Medium>
          {onClose && (
            <IconButton
              variant="outline"
              size="S"
              icon="close"
              onClick={onClose}
              className="hidden! md:flex! md:shrink-0"
            />
          )}
        </div>
        {typeof subtitle === 'string' ? (
          <Typography.Text200Regular
            as="p"
            className="w-full text-center text-text-secondary md:text-left"
          >
            {subtitle}
          </Typography.Text200Regular>
        ) : (
          <div className="w-full text-center text-text-secondary md:text-left">
            {subtitle}
          </div>
        )}
      </div>
    );
  },
);

AuthHeader.displayName = 'AuthHeader';
